import { useState } from "react";
import { apiPost } from "@/pages/admin/utils";

interface ChangePasswordTabProps {
  token: string;
  onLogout: () => void;
}

export default function ChangePasswordTab({ token, onLogout }: ChangePasswordTabProps) {
  const [form, setForm] = useState({ old_password: "", new_password: "", confirm_password: "" });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState<"success" | "error">("success");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const showMsg = (text: string, type: "success" | "error" = "success") => {
    setMsg(text); setMsgType(type);
    if (type === "error") setTimeout(() => setMsg(""), 4000);
  };

  const handleSubmit = async () => {
    if (!form.old_password || !form.new_password || !form.confirm_password) {
      showMsg("请填写所有字段", "error"); return;
    }
    if (form.new_password !== form.confirm_password) {
      showMsg("两次输入的新密码不一致", "error"); return;
    }
    if (form.new_password.length < 6) {
      showMsg("新密码长度不能少于6位", "error"); return;
    }
    setLoading(true);
    try {
      const data = await apiPost("admin/change-password", token, {
        old_password: form.old_password,
        new_password: form.new_password,
        confirm_password: form.confirm_password,
      });
      if (data.code === 200) {
        showMsg("密码修改成功，即将退出登录...");
        setTimeout(() => { onLogout(); }, 1500);
      } else {
        showMsg(data.msg || "修改失败", "error");
      }
    } catch {
      showMsg("网络错误，请重试", "error");
    }
    setLoading(false);
  };

  const getStrength = () => {
    if (form.new_password.length < 6) return 0;
    if (form.new_password.length < 10) return 1;
    if (/[A-Z]/.test(form.new_password) && /[0-9]/.test(form.new_password)) return 3;
    return 2;
  };

  const strength = getStrength();

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-red-50">
          <i className="ri-lock-password-line text-red-500 text-lg"></i>
        </div>
        <div>
          <h2 className="text-base font-bold text-gray-800">修改密码</h2>
          <p className="text-xs text-gray-400">修改后需重新登录</p>
        </div>
      </div>

      {msg && (
        <div className={`mb-4 p-3 border rounded-xl text-sm flex items-center gap-2 ${
          msgType === "success"
            ? "bg-green-50 border-green-200 text-green-600"
            : "bg-red-50 border-red-200 text-red-600"
        }`}>
          <i className={msgType === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"}></i>
          {msg}
        </div>
      )}

      <div className="bg-white border border-gray-100 rounded-2xl p-5 space-y-4">
        <div>
          <label className="text-xs font-semibold text-gray-600 mb-1.5 block">原密码</label>
          <div className="relative">
            <input
              type={showOld ? "text" : "password"}
              value={form.old_password}
              onChange={e => setForm(prev => ({ ...prev, old_password: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors pr-10"
              placeholder="请输入原密码"
            />
            <button type="button" onClick={() => setShowOld(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center cursor-pointer text-gray-400 hover:text-gray-600">
              <i className={showOld ? "ri-eye-off-line" : "ri-eye-line"}></i>
            </button>
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-600 mb-1.5 block">新密码</label>
          <div className="relative">
            <input
              type={showNew ? "text" : "password"}
              value={form.new_password}
              onChange={e => setForm(prev => ({ ...prev, new_password: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors pr-10"
              placeholder="至少6位"
            />
            <button type="button" onClick={() => setShowNew(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center cursor-pointer text-gray-400 hover:text-gray-600">
              <i className={showNew ? "ri-eye-off-line" : "ri-eye-line"}></i>
            </button>
          </div>
          {form.new_password.length > 0 && (
            <div className="mt-2">
              <div className="flex gap-1 mb-1">
                {[1, 2, 3].map(level => (
                  <div key={level} className={`h-1 flex-1 rounded-full transition-colors ${
                    strength >= level
                      ? level === 1 ? "bg-red-400" : level === 2 ? "bg-orange-400" : "bg-green-400"
                      : "bg-gray-100"
                  }`} />
                ))}
              </div>
              <p className="text-xs text-gray-400">
                {strength === 0 ? "密码太短" : strength === 1 ? "强度：一般" : strength === 2 ? "强度：中等" : "强度：强"}
              </p>
            </div>
          )}
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-600 mb-1.5 block">确认新密码</label>
          <div className="relative">
            <input
              type={showConfirm ? "text" : "password"}
              value={form.confirm_password}
              onChange={e => setForm(prev => ({ ...prev, confirm_password: e.target.value }))}
              onKeyDown={e => e.key === "Enter" && handleSubmit()}
              className={`w-full border rounded-xl px-4 py-3 text-sm outline-none transition-colors pr-10 ${
                form.confirm_password && form.confirm_password !== form.new_password
                  ? "border-red-300 focus:border-red-400"
                  : form.confirm_password && form.confirm_password === form.new_password
                  ? "border-green-300 focus:border-green-400"
                  : "border-gray-200 focus:border-red-400"
              }`}
              placeholder="再次输入新密码"
            />
            <button type="button" onClick={() => setShowConfirm(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center cursor-pointer text-gray-400 hover:text-gray-600">
              <i className={showConfirm ? "ri-eye-off-line" : "ri-eye-line"}></i>
            </button>
          </div>
          {form.confirm_password && form.confirm_password !== form.new_password && (
            <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
              <i className="ri-close-circle-line"></i>两次密码不一致
            </p>
          )}
          {form.confirm_password && form.confirm_password === form.new_password && (
            <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
              <i className="ri-checkbox-circle-line"></i>密码一致
            </p>
          )}
        </div>
      </div>

      <button
        onClick={handleSubmit}
        disabled={loading}
        className="w-full mt-5 py-3 bg-gradient-to-r from-red-500 to-orange-400 text-white rounded-xl text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 whitespace-nowrap"
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-loader-4-line animate-spin"></i>提交中...
          </span>
        ) : (
          <span><i className="ri-lock-line mr-1.5"></i>确认修改密码</span>
        )}
      </button>

      <p className="text-xs text-center text-gray-400 mt-3">修改成功后将自动退出，需重新登录</p>
    </div>
  );
}
